<template>

  <div class="audio-recorder d-flex align-items-center">

    <!-- no mic / permission denied -->
    <template v-if="state === 'error'">
      <i class="fa-solid fa-triangle-exclamation text-danger mr-2"></i>
      <span class="small text-danger flex-grow-1 mr-2">{{ error }}</span>
      <button
        type="button"
        class="btn btn-link btn-sm text-muted p-0"
        :title="$t('Cancel')"
        @click="close">
        <i class="fa-solid fa-xmark"></i>
      </button>
    </template>

    <!-- recording: only stop or discard -->
    <template v-else-if="state === 'recording'">
      <span class="audio-recorder-dot flex-shrink-0 mr-2"></span>
      <span class="text-muted flex-grow-1">{{ $t('Recording') }} · {{ elapsedLabel }}</span>
      <button
        type="button"
        class="btn btn-link btn-sm text-muted p-0 mr-3"
        :title="$t('Discard')"
        @click="close">
        <i class="fa-regular fa-trash-can"></i>
      </button>
      <button
        type="button"
        class="btn btn-primary btn-sm flex-shrink-0"
        :title="$t('Stop')"
        @click="stop">
        <i class="fa-solid fa-stop"></i>
      </button>
    </template>

    <!-- waiting for the mic permission, or encoding what was recorded -->
    <template v-else>
      <i class="fa-solid fa-circle-notch fa-spin text-muted mr-2"></i>
      <span class="small text-muted flex-grow-1 mr-2">{{ waitingLabel }}</span>
      <button
        type="button"
        class="btn btn-link btn-sm text-muted p-0"
        :title="$t('Cancel')"
        @click="close">
        <i class="fa-solid fa-xmark"></i>
      </button>
    </template>

  </div>

</template>

<script>
import {
  Input, Output, Conversion, ALL_FORMATS,
  BlobSource, BufferTarget, OggOutputFormat, canEncodeAudio
} from 'mediabunny'

/*
what every recording is turned into before it leaves the recorder. the browsers
each write something else on their own (webm/opus on chrome, ogg/opus on
firefox, mp4/aac on safari) and whatsapp only takes ogg/opus mono for a voice
note, so what the recorder produced is never uploaded as is: it is decoded and
re-encoded into the one format the api is known to accept
*/
const TARGET_MIME = 'audio/ogg'
const TARGET_EXTENSION = 'ogg'
const TARGET_AUDIO = {
  codec: 'opus',
  // whatsapp refuses a stereo voice note
  numberOfChannels: 1,
  sampleRate: 48000,
  bitrate: 32000
}

/*
stops on its own so a forgotten recorder never grows past the upload limit the
composer enforces (5MB). two minutes stay well under it at any bitrate the
browsers pick on their own
*/
const MAX_DURATION = 120

/*
toOggOpus
decodes whatever the recorder wrote and re-encodes it as ogg/opus. answers null
when the browser has no encoder for it, the caller falls back to the recording
*/
async function toOggOpus (blob) {
  // WebCodecs is what does the encoding: on a browser without it, or without
  // opus in it, there is nothing to convert with
  const encodable = await canEncodeAudio(TARGET_AUDIO.codec, {
    numberOfChannels: TARGET_AUDIO.numberOfChannels,
    sampleRate: TARGET_AUDIO.sampleRate,
    bitrate: TARGET_AUDIO.bitrate
  })

  if (!encodable) {
    return null
  }

  const target = new BufferTarget()

  const conversion = await Conversion.init({
    input: new Input({
      source: new BlobSource(blob),
      // the container is detected, not assumed: the recorder picked it
      formats: ALL_FORMATS
    }),
    output: new Output({
      format: new OggOutputFormat(),
      target: target
    }),
    audio: TARGET_AUDIO,
    // the discarded tracks are handled right below, no console noise wanted
    showWarnings: false
  })

  // the recording holds nothing this browser can decode: executing would throw
  if (!conversion.isValid) {
    return null
  }

  await conversion.execute()

  return new Blob([target.buffer], { type: TARGET_MIME })
}

/*
recordedFile
the recording as it came out of the recorder, used when the conversion could
not run. the recorder answers with the codec parameter attached, the api only
takes the mime type itself
*/
function recordedFile (blob, name) {
  const mime = (blob.type || 'audio/webm').split(';')[0]
  const subtype = mime.split('/')[1]

  return new File([blob], name + '.' + (subtype === 'mp4' ? 'm4a' : subtype), { type: mime })
}

/*
AudioRecorder
records from the mic and hands the recording over as a File on stop.
it never uploads anything: the parent keeps the file until the message is sent
*/
export default {
  name: 'AudioRecorder',
  data () {
    // only what the template reads: the recorder, the stream and the chunks
    // are plain instance fields, there is nothing to gain from observing them
    return {
      recording: false,
      // the recording ended and the file is being built out of the chunks
      stopping: false,
      elapsed: 0,
      error: null
    }
  },
  computed: {
    // starting while the mic is being granted, then recording until stop
    state () {
      if (this.error) {
        return 'error'
      }

      if (this.recording) {
        return 'recording'
      }

      return 'waiting'
    },
    // the two waits the recorder goes through, before and after the recording
    waitingLabel () {
      return this.stopping ? this.$t('Preparing the audio') : this.$t('Starting the recorder')
    },
    elapsedLabel () {
      const minutes = Math.floor(this.elapsed / 60)
      const seconds = this.elapsed % 60
      return minutes + ':' + (seconds < 10 ? '0' : '') + seconds
    }
  },
  created () {
    this.recorder = null
    this.stream = null
    this.chunks = []
    this.timer = null
    this.startedAt = 0
    // the conversion outlives the recorder: it must not hand a file over to a
    // parent that already dropped the recording
    this.canceled = false
  },
  mounted () {
    // the user already asked for the recorder by opening it
    this.start()
  },
  beforeDestroy () {
    this.canceled = true
    this.release()
  },
  methods: {
    /*
    start
    asks for the mic and starts recording
    */
    start () {
      const supported = navigator.mediaDevices && navigator.mediaDevices.getUserMedia && window.MediaRecorder

      if (!supported) {
        this.error = this.$t('This browser does not support audio recording')
        return
      }

      navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        this.stream = stream
        this.chunks = []
        // no format is asked for: whatever the browser records best is what it
        // records, the file is converted once the recording is over
        this.recorder = new window.MediaRecorder(stream)

        this.recorder.ondataavailable = (event) => {
          if (event.data && event.data.size) {
            this.chunks.push(event.data)
          }
        }

        this.recorder.onstop = () => {
          this.buildFile()
        }

        // the mic can go away mid recording: without this the recorder would
        // stay on screen recording nothing
        this.recorder.onerror = () => {
          this.release()
          this.error = this.$t('The recording failed')
        }

        this.recorder.start()
        this.recording = true
        this.startTimer()
      })
      .catch(() => {
        this.error = this.$t('Could not access the microphone')
      })
    },
    /*
    stop
    ends the recording: the file is built on the recorder onstop and handed
    over right away, there is nothing left to confirm
    */
    stop () {
      if (!this.recording || !this.recorder) {
        return
      }

      this.recording = false
      this.stopping = true
      this.stopTimer()
      this.recorder.stop()
    },
    /*
    buildFile
    turns the recorded chunks into the file and hands it to the parent, where
    it becomes an attachment waiting for the next send
    */
    async buildFile () {
      const name = 'audio-' + Date.now()
      const recorded = new Blob(this.chunks, { type: this.recorder.mimeType })

      // the chunks are already in the blob, and the mic has no reason to stay
      // on while the recording is being encoded
      this.release()

      if (!recorded.size) {
        this.error = this.$t('The recording failed')
        return
      }

      // release() cleared it: the recorder is done, the encoding is not
      this.stopping = true

      let converted = null

      try {
        converted = await toOggOpus(recorded)
      } catch (error) {
        converted = null
      }

      // the recorder was closed, or the whole composer went away, while the
      // recording was being encoded: there is nobody left to hand it to
      if (this.canceled) {
        return
      }

      this.stopping = false

      // nothing to convert with: the recording still goes out, in whatever the
      // browser wrote it in
      this.$emit('record', converted
        ? new File([converted], name + '.' + TARGET_EXTENSION, { type: TARGET_MIME })
        : recordedFile(recorded, name))
    },
    /*
    close
    drops the recording, going on or already done, and closes the recorder
    */
    close () {
      this.canceled = true

      if (this.recorder && this.recording) {
        this.recorder.stop()
      }

      this.release()
      this.$emit('close')
    },
    /*
    startTimer
    counted from the start time instead of from the ticks: a background tab
    throttles the interval, and a recording that outlives the limit is one
    the composer refuses on send
    */
    startTimer () {
      this.elapsed = 0
      this.startedAt = Date.now()
      this.timer = setInterval(() => {
        this.elapsed = Math.floor((Date.now() - this.startedAt) / 1000)
        if (this.elapsed >= MAX_DURATION) {
          this.stop()
        }
      }, 1000)
    },
    stopTimer () {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },
    /*
    release
    turns the mic off and drops the recorder
    */
    release () {
      this.recording = false
      this.stopping = false
      this.stopTimer()

      // no file is wanted anymore: stopping the stream below would otherwise
      // fire onstop and build one out of a recorder that is already gone
      if (this.recorder) {
        this.recorder.ondataavailable = null
        this.recorder.onstop = null
        this.recorder.onerror = null
        this.recorder = null
      }

      if (this.stream) {
        this.stream.getTracks().forEach((track) => { track.stop() })
        this.stream = null
      }

      this.chunks = []
    }
  }
}
</script>

<style scoped>
.audio-recorder {
  min-height: 40px;
}

/* blinking dot while the mic is on */
.audio-recorder-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #dc3545;
  animation: audio-recorder-blink 1s ease-in-out infinite;
}

@keyframes audio-recorder-blink {
  50% {
    opacity: .2;
  }
}

.audio-recorder .btn-link {
  font-size: 18px;
  line-height: 1;
}

.audio-recorder .btn-link:hover:not(:disabled) {
  color: #2b8aef !important;
}
</style>



// WEBPACK FOOTER //
// src/components/Messaging/composer/AudioRecorder.vue